import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Code2, Info, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWizardTelemetry } from '@/hooks/useWizardTelemetry';
import { cn } from '@/lib/utils';
import { generateVideoEmbedCodes } from '@/utils/generateVideoEmbedCode';

interface CopyCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'ab-test' | 'hooktrack' | 'video' | 'vsl' | string;
  entityId: string;
  entityName: string;
  jsCode?: string;
  userId?: string;
  videoOrigin?: string;
  iframeCode?: string;
  hideJsTab?: boolean;
  defaultTab?: string;
  haveHeadline?: boolean;
  isHTMLSelected?: boolean;
  headlineHtmlCode?: string;
  headlineText?: string;
  subHeadlineText?: string;
  headlineColor?: string;
  subHeadlineColor?: string;
  headlineSize?: string | number;
  subHeadlineSize?: string | number;
  headlineTypeFont?: string;
  headlineFontWeight?: string;
  headlineShowThroughout?: boolean;
  headlineInitialDelay?: string;
  headlineEndDelay?: string;
}

export function CopyCodeModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  jsCode: jsCodeProp,
  userId,
  videoOrigin = 'ORIGINAL',
}: CopyCodeModalProps) {
  useLanguage();
  const { toast } = useToast();
  const { trackEvent } = useWizardTelemetry();
  const [copied, setCopied] = useState(false);
  const [copiedPerformance, setCopiedPerformance] = useState(false);
  const [isHoveringPerformance, setIsHoveringPerformance] = useState(false);

  // Estados para código gerado dinamicamente
  const [generatedJsCode, setGeneratedJsCode] = useState<string>('');
  const [isLoadingCodes, setIsLoadingCodes] = useState(false);

  useEffect(() => {
    const generateCodes = async () => {
      if (!userId) {
        setGeneratedJsCode(jsCodeProp || '');
        return;
      }

      setIsLoadingCodes(true);
      try {
        // O embed é apenas o snippet do Web Component (<hostvsl-player>). Toda
        // a config (headline, button, responsive, timing, TESTHEADLINE) é
        // lida do videoInformations.js em runtime — não precisamos mais
        // pré-fetchar nada aqui.
        const codes = await generateVideoEmbedCodes({
          videoId: entityId,
          userId,
          videoOrigin,
        });
        setGeneratedJsCode(codes.jsCode);
      } catch (error) {
        console.error('Erro ao gerar código de incorporação:', error);
        setGeneratedJsCode(jsCodeProp || '');
      } finally {
        setIsLoadingCodes(false);
      }
    };

    if (isOpen) {
      generateCodes();
    }
  }, [isOpen, userId, entityId, videoOrigin, jsCodeProp]);

  const jsCode = generatedJsCode || jsCodeProp || '';

  const headConfig = `<script>
  !function (w, p) {
    w._plt = w._plt || (p && p.timeOrigin ? p.timeOrigin + p.now() : Date.now());
  }(window, performance);
</script>
<link rel="preload" href="${import.meta.env.VITE_API_PLAYJS_BUNNY}${import.meta.env.VITE_API_PLAYJS_VERSION}/hostvsl-player.js" as="script">
<link rel="preconnect" href="${import.meta.env.VITE_API_PLAYJS_BUNNY}" crossorigin>
<link rel="dns-prefetch" href="${import.meta.env.VITE_API_PLAYJS_BUNNY}">
<link rel="preload" href="${import.meta.env.VITE_BUCKET_CDN}${userId}/${entityId}/master.m3u8" as="fetch" crossorigin>
<link rel="preconnect" href="${import.meta.env.VITE_BUCKET_CDN}" crossorigin>
<link rel="dns-prefetch" href="${import.meta.env.VITE_BUCKET_CDN}">
<link rel="dns-prefetch" href="${import.meta.env.VITE_API}">
<link rel="preconnect" href="${import.meta.env.VITE_API}" crossorigin>`;

  // Track modal opened
  React.useEffect(() => {
    if (isOpen) {
      trackEvent('copy_code_opened', { 
        entityType, 
        entityId,
        context: entityType 
      });
    }
  }, [isOpen, trackEvent, entityType, entityId]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(jsCode)
      .then(() => {
        setCopied(true);
        toast({
          title: translateInterfaceText("Copiado com sucesso!"),
        });
        
        trackEvent('copy_code_copied', {
          entityType,
          entityId,
          tab: 'js',
          context: entityType
        });
        
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch(() => {
        toast({
          title: translateInterfaceText("Erro ao copiar"),
          description: translateInterfaceText("Não foi possível copiar o código. Tente novamente."),
          variant: "destructive"
        });
      });
  };

  const handleCopyPerformanceCode = () => {
    navigator.clipboard.writeText(headConfig)
      .then(() => {
        setCopiedPerformance(true);
        toast({
          title: translateInterfaceText("Copiado com sucesso!"),
        });
        setTimeout(() => {
          setCopiedPerformance(false);
        }, 2000);
      })
      .catch(() => {
        toast({
          title: translateInterfaceText("Erro ao copiar"),
          description: translateInterfaceText("Não foi possível copiar o código. Tente novamente."),
          variant: "destructive"
        });
      });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        hideCloseButton={true}
        className="
          w-[calc(100%-32px)] sm:w-[560px]
          sm:max-w-[560px]
          max-h-[100vh] sm:max-h-[90vh]
          p-4 sm:p-5 md:p-7
          rounded-none sm:rounded-2xl
          overflow-hidden
          animate-in fade-in-0 slide-in-from-bottom-4 duration-200
          box-border
        ">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold text-[#111827]">
            {translateInterfaceText("Incorpore seu vídeo no site!")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3.5 sm:space-y-5 overflow-x-hidden">
          {/* Label JS */}
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-[#1F6BFF]" />
            <span className="text-sm font-medium text-[#374151]">{translateInterfaceText("JS")}</span>
          </div>

          {/* Container de Código */}
          <div
            className="relative cursor-pointer group"
            onClick={!isLoadingCodes ? handleCopyCode : undefined}
          >
            <pre className="
              bg-[#EEF4FF]
              p-3 sm:p-4
              rounded-[10px]
              border border-[#D6E4FF]
              text-xs font-mono
              text-[#374151]
              max-h-[120px] sm:max-h-[140px]
              overflow-y-auto overflow-x-hidden
              whitespace-pre-wrap break-words
              transition-all duration-150
              group-hover:border-[#1D4FFF] group-hover:bg-[#E8F0FF]
            ">
              {isLoadingCodes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#1D4FFF]" />
                  <span className="ml-2 text-[#6B7280]">{translateInterfaceText("Gerando código...")}</span>
                </div>
              ) : (
                jsCode
              )}
            </pre>
          </div>

          {/* Bloco Como Usar - Compacto */}
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-[#1D4FFF] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#4B5563] leading-relaxed">
              <span className="font-medium text-[#111827]">{translateInterfaceText("Como usar:")}</span> {" "}{translateInterfaceText("Cole o código abaixo na tag")}{' '}
              <code className="px-1 py-0.5 bg-[#F3F4F6] rounded text-[#1D4FFF] font-mono text-xs">&lt;head&gt;</code>{' '}
              {translateInterfaceText("para melhorar o carregamento.")}{' '}
              <a href="#" className="text-[#1D4FFF] hover:underline font-medium">
                {translateInterfaceText("Converse com a gente!")}</a>
            </p>
          </div>

          {/* Código Performance - Container Secundário Compacto */}
          <div
            className="relative cursor-pointer group"
            onClick={handleCopyPerformanceCode}
          >
            <pre className="
              bg-[#EEF4FF]
              p-3
              rounded-[10px]
              border border-[#D6E4FF]
              text-xs font-mono
              text-[#374151]
              max-h-[180px]
              overflow-y-auto overflow-x-hidden
              whitespace-pre-wrap break-words
              transition-all duration-150
              group-hover:border-[#1D4FFF] group-hover:bg-[#E8F0FF]
            ">
              {headConfig}
            </pre>
          </div>
        </div>

        {/* Footer com botões de ação */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 mt-2 border-t border-[#E5E7EB] pb-0">
          <Button
            onClick={handleCopyCode}
            disabled={isLoadingCodes}
            className={cn(
              "flex-1 h-11 font-medium rounded-xl gap-2 transition-all duration-150",
              isHoveringPerformance
                ? "bg-white border border-[#D6E4FF] text-[#1D4FFF]"
                : "bg-[#1D4FFF] text-white"
            )}
          >
            {isLoadingCodes ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {translateInterfaceText("Carregando...")}</>
            ) : copied ? (
              <>
                <Check className="h-4 w-4" />
                {translateInterfaceText("Copiado!")}</>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                {translateInterfaceText("Copiar código JS")}</>
            )}
          </Button>
          
                <Button 
                  onClick={handleCopyPerformanceCode}
                  onMouseEnter={() => setIsHoveringPerformance(true)}
                  onMouseLeave={() => setIsHoveringPerformance(false)}
                  className={cn(
                    "flex-1 h-11 font-medium rounded-xl gap-2 transition-all duration-150",
                    isHoveringPerformance 
                      ? "bg-[#1D4FFF] text-white border-[#1D4FFF]"
                      : "bg-white border border-[#E4E7EC] text-[#6B7280]"
                  )}
                >
            {copiedPerformance ? (
              <>
                <Copy className="h-4 w-4" />
                {translateInterfaceText("Copiado!")}</>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                {translateInterfaceText("Copiar código performance")}</>
            )}
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
}
